export type OptionsWithAll<ov> = Option<ov | 'All'>[]

export type Options<ov> = Option<ov>[]

export type Option<ov> = {
  name: string
  value: ov
  color?: string
  isSettable?: boolean
  number?: number
}

export type IDMap<T> = {
  [id: string]: T
}

export type Group<T, ov = string> = {
  list: T[]
  map: IDMap<T>
  options: Options<ov>
  optionsWithAll: OptionsWithAll<ov>
  optionsWithNull: Options<ov | null>
  count: number
}

export const getOptions = <T>(
  data: T[],
  nameKey: keyof T,
  valueKey: keyof T
): { name: string; value: string }[] => {
  return data.map((item) => ({
    name: item[nameKey] as string,
    value: item[valueKey] as string
  }))
}

export function getEmptyGroup<T, ov = string>(): Group<T, ov> {
  return {
    list: [],
    map: {},
    options: [],
    optionsWithAll: [{ name: 'All', value: 'All' }],
    optionsWithNull: [{ name: 'Select', value: null }],
    count: 0
  }
}

export function addItemToGroup<
  T extends Record<ValueKey, string | number | null>,
  ValueKey extends string
>(
  group: Group<T, T[ValueKey]>,
  item: T,
  groupKey: string,
  valueKey: ValueKey,
  optionName: string,
  sortByKey?: string
) {
  group.map[groupKey] = item
  // Add element to array in alphabetical position based on sort by key - if not provided use value key
  const sortKey = sortByKey || valueKey
  const getSortValue = (el: T) =>
    (el as Record<string, string | number>)[sortKey]
  const itemSortValue = getSortValue(item)
  const index = group.list.findIndex((element) => {
    const elementSortValue = getSortValue(element)
    if (
      typeof elementSortValue === 'number' &&
      typeof itemSortValue === 'number'
    ) {
      return elementSortValue > itemSortValue
    }
    return String(elementSortValue) > String(itemSortValue)
  })

  const option = {
    name: optionName,
    value:
      item[valueKey] ||
      ((item as Record<string, string | number>)['id'] as T[ValueKey])
  }

  if (index !== -1) {
    group.list.splice(index, 0, item)
  } else {
    group.list.push(item)
  }

  group.options.push(option)
  group.optionsWithAll.push(option)
  group.optionsWithNull.push(option)

  group.count++
}
